import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { fixClienteDisplay } from '../utils/textEncoding';

/**
 * Direcciones/sedes opcionales bajo un cliente.
 */
export default function ClienteSedesPanel({ cliente, onToast, onSelectSede, onSedesChanged }) {
  const padreId = Number(cliente?.id);
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [etiqueta, setEtiqueta] = useState('');
  const [direccion, setDireccion] = useState('');
  const [ciudad, setCiudad] = useState(cliente?.ciudad || '');
  const [zona, setZona] = useState(cliente?.zona || '');

  useEffect(() => {
    setCiudad(cliente?.ciudad || '');
    setZona(cliente?.zona || '');
  }, [cliente?.id, cliente?.ciudad, cliente?.zona]);

  useEffect(() => {
    if (!(padreId > 0) || cliente?.padre_id || cliente?.es_sede) {
      setSedes([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api
      .getSedes(padreId)
      .then((r) => {
        if (!cancelled) setSedes((r.data || []).map(fixClienteDisplay));
      })
      .catch((err) => {
        if (!cancelled) {
          const msg =
            err?.status === 404
              ? 'El API del hosting no tiene sedes (falta actualizar/reiniciar). Sube api-completo.zip.'
              : err.message || 'No se pudieron cargar las direcciones';
          onToast?.(msg, 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [padreId, cliente?.padre_id, cliente?.es_sede, onToast]);

  if (!(padreId > 0) || cliente?.padre_id || cliente?.es_sede) return null;

  async function guardarSede(e) {
    e.preventDefault();
    if (!direccion.trim() && !etiqueta.trim()) {
      onToast?.('Escribe la dirección o un nombre de sede', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await api.crearSede(padreId, {
        etiqueta: etiqueta.trim() || undefined,
        direccion: direccion.trim() || undefined,
        ciudad: ciudad.trim() || undefined,
        zona: zona.trim() || undefined,
      });
      setSedes((prev) => [...prev, fixClienteDisplay(res.data)]);
      setEtiqueta('');
      setDireccion('');
      setOpenForm(false);
      onSedesChanged?.(1);
      onToast?.('Dirección guardada', 'ok');
    } catch (err) {
      let msg =
        err?.status === 404
          ? 'El API del hosting no tiene sedes (falta actualizar/reiniciar). Sube api-completo.zip y reinicia Node.'
          : err?.status === 503 && err?.code === 'SCHEMA_SEDES'
            ? err.message
            : err.message || 'No se pudo guardar la dirección';
      if (/Data too long for column|ER_DATA_TOO_LONG|ER_|SQL|mysql/i.test(msg)) {
        msg =
          'No se pudo guardar la dirección (API desactualizado). Sube api-parche-sedes.zip o api-completo.zip y reinicia Node en cPanel.';
      }
      onToast?.(msg, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cliente-sedes-panel" onClick={(e) => e.stopPropagation()}>
      <div className="cliente-sedes-head">
        <strong>Direcciones / sedes</strong>
      </div>

      {loading ? <p className="muted">Cargando…</p> : null}

      {!loading && !sedes.length ? (
        <p className="muted cliente-sedes-empty">
          Aún no hay direcciones extra. Agrega dónde está ubicado el cliente.
        </p>
      ) : null}

      <ul className="cliente-sedes-list">
        {sedes.map((s) => (
          <li key={s.id} className="cliente-sede-card">
            <div>
              <div className="cliente-sede-card-title">
                <span className="cliente-sede-badge">Sucursal</span>
                <strong>{s.sede_etiqueta || s.etiqueta || 'Sede'}</strong>
              </div>
              {s.direccion ? <span>{s.direccion}</span> : null}
              <span className="muted">
                {[
                  s.ciudad ? `Ciudad: ${s.ciudad}` : null,
                  s.zona ? `Zona: ${s.zona}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </span>
              <span className="muted">
                Tel: {s.telefono1 || s.telefono || cliente?.telefono1 || cliente?.telefono || '—'}
                {' · '}
                Vendedor: {s.vendedor_nombre || cliente?.vendedor_nombre || 'Sin asignar'}
              </span>
              {s.padre_codigo ? (
                <span className="muted">Cliente padre: {s.padre_codigo}</span>
              ) : null}
            </div>
            <div className="cliente-sedes-actions">
              {onSelectSede ? (
                <button type="button" className="btn-primary btn-sm" onClick={() => onSelectSede(s)}>
                  Usar
                </button>
              ) : null}
              {/* Solo admin/oficina quitan sedes — vendedor solo crea. */}
            </div>
          </li>
        ))}
      </ul>

      {openForm ? (
        <form className="cliente-sedes-form" onSubmit={guardarSede}>
          <label>
            Nombre de sede (opcional)
            <input
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
              placeholder="Ej. Local Centro / Bodega"
            />
          </label>
          <label>
            Dirección / ubicación *
            <input
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Calle, barrio, punto de referencia…"
              required={!etiqueta.trim()}
            />
          </label>
          <div className="cliente-sedes-form-row">
            <label>
              Ciudad
              <input value={ciudad} onChange={(e) => setCiudad(e.target.value)} />
            </label>
            <label>
              Barrio / zona
              <input value={zona} onChange={(e) => setZona(e.target.value)} />
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpenForm(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar dirección'}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="btn-ghost cliente-sedes-add" onClick={() => setOpenForm(true)}>
          + Agregar dirección / sede
        </button>
      )}
    </div>
  );
}
